import React, { forwardRef, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { InputSelect } from "./InputSelect";

type Subdivision = {
  code: string;
  name: string;
  // Only set where German differs from the official name, which is rare.
  nameDe?: string;
};

// ISO 3166-2 top-level subdivisions, taken from the Debian iso-codes project
// (tag v4.20.1) and cross-checked against wooorm/iso-3166 and esosedi/3166.
// Codes and names are the ISO official ones, so a value here is safe to hand
// to anything that speaks ISO 3166-2.
//
// Two deliberate departures from the raw source: names recorded in sort order
// ("Virgin Islands, U.S.") are written in reading order, and US-GA keeps its
// English name because the German catalogue mistranslates it as "Georgien",
// which is the country Georgia rather than the state.
export const SUBDIVISIONS: Record<string, Subdivision[]> = {
  // Germany (16)
  DE: [
    { code: "DE-BW", name: "Baden-Württemberg" },
    { code: "DE-BY", name: "Bayern" },
    { code: "DE-BE", name: "Berlin" },
    { code: "DE-BB", name: "Brandenburg" },
    { code: "DE-HB", name: "Bremen" },
    { code: "DE-HH", name: "Hamburg" },
    { code: "DE-HE", name: "Hessen" },
    { code: "DE-MV", name: "Mecklenburg-Vorpommern" },
    { code: "DE-NI", name: "Niedersachsen" },
    { code: "DE-NW", name: "Nordrhein-Westfalen" },
    { code: "DE-RP", name: "Rheinland-Pfalz" },
    { code: "DE-SL", name: "Saarland" },
    { code: "DE-SN", name: "Sachsen" },
    { code: "DE-ST", name: "Sachsen-Anhalt" },
    { code: "DE-SH", name: "Schleswig-Holstein" },
    { code: "DE-TH", name: "Thüringen" },
  ],

  // United Kingdom (4)
  GB: [
    { code: "GB-ENG", name: "England" },
    { code: "GB-NIR", name: "Northern Ireland", nameDe: "Nordirland" },
    { code: "GB-SCT", name: "Scotland", nameDe: "Schottland" },
    { code: "GB-WLS", name: "Wales" },
  ],

  // United States (57)
  US: [
    { code: "US-AL", name: "Alabama" },
    { code: "US-AK", name: "Alaska" },
    { code: "US-AS", name: "American Samoa", nameDe: "Amerikanisch-Samoa" },
    { code: "US-AZ", name: "Arizona" },
    { code: "US-AR", name: "Arkansas" },
    { code: "US-CA", name: "California", nameDe: "Kalifornien" },
    { code: "US-CO", name: "Colorado" },
    { code: "US-CT", name: "Connecticut" },
    { code: "US-DE", name: "Delaware" },
    { code: "US-DC", name: "District of Columbia" },
    { code: "US-FL", name: "Florida" },
    { code: "US-GA", name: "Georgia" },
    { code: "US-GU", name: "Guam" },
    { code: "US-HI", name: "Hawaii" },
    { code: "US-ID", name: "Idaho" },
    { code: "US-IL", name: "Illinois" },
    { code: "US-IN", name: "Indiana" },
    { code: "US-IA", name: "Iowa" },
    { code: "US-KS", name: "Kansas" },
    { code: "US-KY", name: "Kentucky" },
    { code: "US-LA", name: "Louisiana" },
    { code: "US-ME", name: "Maine" },
    { code: "US-MD", name: "Maryland" },
    { code: "US-MA", name: "Massachusetts" },
    { code: "US-MI", name: "Michigan" },
    { code: "US-MN", name: "Minnesota" },
    { code: "US-MS", name: "Mississippi" },
    { code: "US-MO", name: "Missouri" },
    { code: "US-MT", name: "Montana" },
    { code: "US-NE", name: "Nebraska" },
    { code: "US-NV", name: "Nevada" },
    { code: "US-NH", name: "New Hampshire" },
    { code: "US-NJ", name: "New Jersey" },
    { code: "US-NM", name: "New Mexico" },
    { code: "US-NY", name: "New York" },
    { code: "US-NC", name: "North Carolina" },
    { code: "US-ND", name: "North Dakota" },
    { code: "US-MP", name: "Northern Mariana Islands", nameDe: "Nördliche Marianen" },
    { code: "US-OH", name: "Ohio" },
    { code: "US-OK", name: "Oklahoma" },
    { code: "US-OR", name: "Oregon" },
    { code: "US-PA", name: "Pennsylvania" },
    { code: "US-PR", name: "Puerto Rico" },
    { code: "US-RI", name: "Rhode Island" },
    { code: "US-SC", name: "South Carolina" },
    { code: "US-SD", name: "South Dakota" },
    { code: "US-TN", name: "Tennessee" },
    { code: "US-TX", name: "Texas" },
    { code: "US-VI", name: "U.S. Virgin Islands" },
    { code: "US-UM", name: "United States Minor Outlying Islands" },
    { code: "US-UT", name: "Utah" },
    { code: "US-VT", name: "Vermont" },
    { code: "US-VA", name: "Virginia" },
    { code: "US-WA", name: "Washington" },
    { code: "US-WV", name: "West Virginia" },
    { code: "US-WI", name: "Wisconsin" },
    { code: "US-WY", name: "Wyoming" },
  ],
};

export const hasSubdivisions = (countryIso: string | null | undefined): boolean =>
  !!countryIso && countryIso in SUBDIVISIONS;

type SubdivisionSelectProps = {
  countryIso: string;
  name: string;
  value?: string | undefined;
  disabled?: boolean;
  onChange?: (value: string) => void;
  dataTestPrefix?: string;
};

export const SubdivisionSelect = forwardRef<HTMLSelectElement, SubdivisionSelectProps>(
  (props, ref) => {
    const { t, i18n } = useTranslation("common");
    const dataTestPrefix = props.dataTestPrefix ?? props.name;

    const options = useMemo(() => {
      const preferGerman = i18n.language.startsWith("de");
      return (SUBDIVISIONS[props.countryIso] ?? [])
        .map((subdivision) => ({
          label: (preferGerman && subdivision.nameDe) || subdivision.name,
          value: subdivision.code,
        }))
        .sort((a, b) => a.label.localeCompare(b.label, i18n.language));
    }, [props.countryIso, i18n.language]);

    return (
      <InputSelect
        ref={ref}
        placeholder={t("forms.selectState")}
        name={props.name}
        value={props.value}
        options={options}
        disabled={props.disabled}
        onChange={(value) => props.onChange?.(value)}
        dataTestPrefix={dataTestPrefix}
      />
    );
  },
);
